import { createContext, useState, useCallback, useRef, ReactNode } from "react";
import * as RadixToast from "@radix-ui/react-toast";
import { ToastItem, ToastType } from "@/components/ToastItem";

// How long the CSS slide-out animation runs (must match toast-exit in index.css)
const EXIT_ANIMATION_MS = 320;

interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  header?: string;
  duration?: number;
  placement?: "top-center" | "top-right" | "bottom-right";
  open: boolean;
}

interface ToastContextType {
  showToast: (toast: Omit<ToastProps, "id" | "open">) => void;
  hideToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    timers.current.delete(id);
  }, []);

  // Step 1: flip open→false so Radix sets data-state="closed" and the CSS
  //         exit animation plays. Step 2: remove from state after animation.
  const hideToast = useCallback(
    (id: string) => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)));
      const t = window.setTimeout(() => removeToast(id), EXIT_ANIMATION_MS);
      timers.current.set(id, t);
    },
    [removeToast],
  );

  const showToast = useCallback(
    ({
      type,
      message,
      duration = 5000,
      placement = "top-right",
      header,
    }: Omit<ToastProps, "id" | "open">) => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [
        ...prev,
        { id, type, message, duration, placement, header, open: true },
      ]);
      // Auto-dismiss after duration; the animation then plays for EXIT_ANIMATION_MS
      const t = window.setTimeout(() => hideToast(id), duration);
      timers.current.set(id, t);
    },
    [hideToast],
  );

  const placement = toasts[toasts.length - 1]?.placement || "top-right";

  const placementClass = {
    "top-center": "top-0 left-1/2 -translate-x-1/2",
    "top-right": "top-4 right-4",
    "bottom-right": "bottom-4 right-4",
  }[placement];

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <RadixToast.Provider swipeDirection="up">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            {...toast}
            onClose={() => hideToast(toast.id)}
            duration={toast.duration || 5000}
          />
        ))}
        {/* Above z-50 -- drawers (AppDrawer) and the full-screen modals sit at
            z-50 themselves, so a toast raised from inside one has to outrank
            them or it renders behind the panel. */}
        <RadixToast.Viewport
          className={`fixed w-1/2 max-w-[40rem] z-[100] flex flex-col gap-2 ${placementClass}`}
        />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
};
